import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import { tInvCdf } from '../../tests/test-runner';

// ---------------------------------------------------------------------------
// Table configuration
// ---------------------------------------------------------------------------

const DF_VALUES: readonly number[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 40, 60,
  70, 80, 90, 100, 1000,
];

interface AlphaColumn {
  readonly twoTailed: number;
  readonly oneTailed: number;
}

const ALPHA_COLUMNS: readonly AlphaColumn[] = [
  { twoTailed: 0.1, oneTailed: 0.05 },
  { twoTailed: 0.05, oneTailed: 0.025 },
  { twoTailed: 0.02, oneTailed: 0.01 },
  { twoTailed: 0.01, oneTailed: 0.005 },
];

const Z_APPROX_DF = 100_000;

interface TableRow {
  readonly df: number | null; // null = z-row (∞)
  readonly label: string;
  readonly values: readonly string[];
}

function buildRow(df: number | null): TableRow {
  const effectiveDf = df ?? Z_APPROX_DF;
  const values = ALPHA_COLUMNS.map((col) => {
    return tInvCdf(1 - col.twoTailed / 2, effectiveDf).toFixed(4);
  });
  return { df, label: df !== null ? String(df) : '∞', values };
}

const TABLE_ROWS: readonly TableRow[] = [...DF_VALUES.map((df) => buildRow(df)), buildRow(null)];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'dojo-critical-values-table',
  standalone: true,
  imports: [],
  templateUrl: './critical-values-table.component.html',
  styleUrl: './critical-values-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CriticalValuesTableComponent {
  readonly highlightDf = input<number | null>(null);
  readonly highlightAlpha = input<number | null>(null);
  readonly alphaHover = output<number | null>();

  readonly columns = ALPHA_COLUMNS;
  readonly rows = TABLE_ROWS;

  // ── Hover state ───────────────────────────────────────────────────────

  private readonly hoverColIdx = signal(-1);

  // ── Highlight indices ─────────────────────────────────────────────────

  readonly highlightRowIdx = computed(() => {
    const df = this.highlightDf();
    if (df === null) {
      return -1;
    }

    // Exact match first
    const exact = TABLE_ROWS.findIndex((r) => r.df === df);
    if (exact >= 0) {
      return exact;
    }

    // Closest match (for non-integer Welch df)
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < TABLE_ROWS.length; i++) {
      const rowDf = TABLE_ROWS[i].df;
      if (rowDf === null) {
        continue;
      }
      const dist = Math.abs(rowDf - df);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }
    return bestIdx;
  });

  readonly highlightColIdx = computed(() => {
    const alpha = this.highlightAlpha();
    if (alpha === null) {
      return -1;
    }
    return ALPHA_COLUMNS.findIndex((c) => Math.abs(c.twoTailed - alpha) < 0.001);
  });

  onColumnEnter(ci: number): void {
    this.hoverColIdx.set(ci);
    this.alphaHover.emit(ALPHA_COLUMNS[ci].twoTailed);
  }

  onColumnLeave(): void {
    this.hoverColIdx.set(-1);
    this.alphaHover.emit(null);
  }

  isHoverCol(ci: number): boolean {
    return ci === this.hoverColIdx();
  }

  isHighlightRow(ri: number): boolean {
    return ri === this.highlightRowIdx();
  }

  isHighlightCol(ci: number): boolean {
    return ci === this.highlightColIdx();
  }

  isHighlightCell(ri: number, ci: number): boolean {
    return this.isHighlightRow(ri) && this.isHighlightCol(ci);
  }
}
